/**
 * Risk Assessment scoring constants and helpers.
 *
 * Source: Docs/Risk Assessments/HRN/HRN Description.pdf
 *
 *   HRN = LO × FE × DPH × NP
 *
 *   LO  Likelihood of Occurrence    (0.033 – 15)
 *   FE  Frequency of Exposure       (0.5   – 5)
 *   DPH Degree of Possible Harm     (0.1   – 15)
 *   NP  Number of Persons at risk   (1     – 12)
 *
 * Risk bands:
 *   0–5    Negligible      (green)
 *   5–50   Low/Significant (yellow)
 *   50–500 High            (orange)
 *   500+   Unacceptable    (red)
 *
 * Stored values are the raw factor numbers (REAL columns on the server).
 * UI presents the user-facing label alongside the numeric value to keep
 * scoring decisions auditable and prevent typos.
 */

export const LO_SCALE = [
  { v: 0.033, l: 'Almost impossible' },
  { v: 1,     l: 'Highly unlikely' },
  { v: 1.5,   l: 'Unlikely' },
  { v: 2,     l: 'Possible' },
  { v: 5,     l: 'Even chance' },
  { v: 8,     l: 'Probable' },
  { v: 10,    l: 'Likely' },
  { v: 15,    l: 'Certain' },
];

export const FE_SCALE = [
  { v: 0.5, l: 'Annually' },
  { v: 1,   l: 'Monthly' },
  { v: 1.5, l: 'Weekly' },
  { v: 2.5, l: 'Daily' },
  { v: 4,   l: 'Hourly' },
  { v: 5,   l: 'Constantly' },
];

export const DPH_SCALE = [
  { v: 0.1, l: 'Scratch / bruise' },
  { v: 0.5, l: 'Laceration / mild ill-effect' },
  { v: 2,   l: 'Minor bone / minor illness (temp)' },
  { v: 4,   l: 'Major bone / major illness (temp)' },
  { v: 6,   l: 'Loss of one limb / eye / hearing (perm)' },
  { v: 10,  l: 'Loss of two limbs / eyes (perm)' },
  { v: 15,  l: 'Fatality' },
];

export const NP_SCALE = [
  { v: 1,  l: '1–2 persons' },
  { v: 2,  l: '3–7 persons' },
  { v: 4,  l: '8–15 persons' },
  { v: 8,  l: '16–50 persons' },
  { v: 12, l: '50+ persons' },
];

/**
 * Band thresholds (`max` is the EXCLUSIVE upper bound). Order matters —
 * `bandFor()` walks bottom-up and returns the first match.
 */
export const BANDS = [
  { max: 5,        key: 'negligible', label: 'Negligible',        tone: 'green'  },
  { max: 50,       key: 'low',        label: 'Low / significant', tone: 'yellow' },
  { max: 500,      key: 'high',       label: 'High',              tone: 'orange' },
  { max: Infinity, key: 'unacceptable', label: 'Unacceptable',    tone: 'red'    },
];

/**
 * Same bands enriched with a printable range string and a one-line suggested
 * action — used by the PDF cover-page guide and the per-page footer strip.
 * Kept separate from `BANDS` so the existing band-lookup logic stays minimal.
 */
export const BANDS_WITH_ACTIONS = [
  { key: 'negligible',   range: '0–5',    label: 'Negligible',        tone: 'green',  suggestedAction: 'Accept; normal vigilance' },
  { key: 'low',          range: '5–50',   label: 'Low / Significant', tone: 'yellow', suggestedAction: 'Document controls + monitor' },
  { key: 'high',         range: '50–500', label: 'High',              tone: 'orange', suggestedAction: 'Mitigate before work proceeds' },
  { key: 'unacceptable', range: '500+',   label: 'Unacceptable',      tone: 'red',    suggestedAction: 'STOP — work prohibited' },
];

/** Tailwind class strings keyed by band tone. Both light + dark mode. */
export const BAND_CLASSES = {
  green:  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-800',
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800',
  orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border border-orange-200 dark:border-orange-800',
  red:    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800',
};

/**
 * Compute HRN from raw factor values.
 * Returns 0 if any factor is missing — used as a "not scored" sentinel.
 */
export function computeHrn(lo, fe, dph, np) {
  const a = Number(lo), b = Number(fe), c = Number(dph), d = Number(np);
  if (!a || !b || !c || !d) return 0;
  return a * b * c * d;
}

/**
 * Return the BANDS entry that contains a given HRN value.
 * 0 is treated as "not scored" — returns the negligible band.
 */
export function bandFor(hrn) {
  const n = Number(hrn) || 0;
  for (const band of BANDS) {
    if (n < band.max) return band;
  }
  return BANDS[BANDS.length - 1];
}

/**
 * Look up the label for a stored numeric factor value.
 * Returns the raw value as a string if no match (defensive — UI should
 * always pick from the scale).
 */
export function labelFor(scale, value) {
  const n = Number(value);
  const hit = scale.find((opt) => opt.v === n);
  return hit ? hit.l : String(value);
}

/**
 * Threshold above which the UI surfaces a "Reduction recommended" hint.
 * Chosen at the mid-point of the Low/Significant band (5–50) — anything in
 * the top half of Low/Sig, the entire High band, and Unacceptable all flag.
 */
export const REDUCTION_THRESHOLD = 25;

export function needsReduction(hrn) {
  return Number(hrn) > REDUCTION_THRESHOLD;
}

/**
 * Format the HRN as a short pill text — e.g. "160 · High".
 * Rounds to 2 dp for display (raw stored value retains full precision).
 * Appends " — Reduction recommended" when the score is above the
 * mid-point of Low/Significant.
 */
export function formatHrnPill(hrn) {
  const n = Number(hrn) || 0;
  const rounded = n < 10 ? Math.round(n * 100) / 100 : Math.round(n);
  const base = `${rounded} · ${bandFor(n).label}`;
  return needsReduction(n) ? `${base} — Reduction recommended` : base;
}

// ─── Hierarchy of control — three-stage methodology (ISO 12100) ────
// Stage 1 — Inherently safe design (eliminate / substitute)
// Stage 2 — Engineering / technical safeguards (guards, interlocks)
// Stage 3 — Information for use (SSOW, training, signage, PPE)
//
// The skill's house style stores controls as one string per row using
// "[Stage N] bullet a; bullet b. [Stage M] bullet c; ..." markers. The
// helpers below parse that format into a structured shape for the editor
// + readers, and serialize back to the same canonical string so nothing
// new lands in the database.

export const CONTROL_STAGES = [
  { n: 1, label: 'Eliminate / Substitute', short: 'Inherently safe design',
    emptyHint: 'None yet. Most effective — try eliminating or substituting the hazard before lower stages.' },
  { n: 2, label: 'Engineering / Guards',   short: 'Technical safeguards',
    emptyHint: 'None yet. Engineered safeguards (guards, interlocks) outperform PPE.' },
  { n: 3, label: 'Inform / SSOW / PPE',    short: 'Information for use',
    emptyHint: 'None yet. PPE and signage are the last line of defence, not a substitute for Stages 1 & 2.' },
];

// "[Stage 1] a; b. [Stage 2] c. [Stage 3] d." → { 1: ['a','b'], 2: ['c'], 3: ['d'] }
// Legacy text with no stage markers is parked in Stage 3 (safest default).
export function parseControlsByStage(text) {
  const result = { 1: [], 2: [], 3: [] };
  if (!text || !String(text).trim()) return result;
  const src = String(text);
  const re = /\[Stage\s*([123])\]\s*/gi;
  const matches = [...src.matchAll(re)];
  if (matches.length === 0) {
    result[3] = src
      .replace(/\.\s*$/, '')
      .split(/;\s*/)
      .map((s) => s.trim())
      .filter(Boolean);
    return result;
  }
  for (let i = 0; i < matches.length; i++) {
    const stage = Number(matches[i][1]);
    const start = matches[i].index + matches[i][0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index : src.length;
    const segment = src.slice(start, end).trim().replace(/\.\s*$/, '');
    const bullets = segment.split(/;\s*/).map((s) => s.trim()).filter(Boolean);
    if (result[stage]) result[stage].push(...bullets);
  }
  return result;
}

export function serializeControlsByStage(stages) {
  const parts = [];
  for (const n of [1, 2, 3]) {
    const items = (stages[n] || [])
      .map((s) => String(s).replace(/[;\n\r]+/g, ' ').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
    if (items.length > 0) parts.push(`[Stage ${n}] ${items.join('; ')}.`);
  }
  return parts.join(' ');
}

// ─── PPE catalogue (Chunk C) ────────────────────────────────────────
// Stable canonical PPE items. Slugs are the storage primitive; labels are
// presentation; emoji are a pragmatic v1 stand-in for proper ISO 7010
// pictograms (data + slugs unchanged when we later swap to inline SVG).
export const PPE_CATALOGUE = [
  { slug: 'head',         label: 'Hard hat',                  emoji: '🪖', iso: 'M014' },
  { slug: 'eye',          label: 'Eye protection',            emoji: '🥽', iso: 'M004' },
  { slug: 'hearing',      label: 'Ear defenders',             emoji: '🎧', iso: 'M003' },
  { slug: 'rpe_ffp3',     label: 'P3 RPE (disposable)',       emoji: '😷', iso: 'M016' },
  { slug: 'rpe_half',     label: 'Half-mask RPE',             emoji: '😷', iso: 'M016' },
  { slug: 'hi_vis',       label: 'Hi-vis',                    emoji: '🦺', iso: 'M015' },
  { slug: 'gloves_cut',   label: 'Cut-resistant gloves',      emoji: '🧤', iso: 'M009' },
  { slug: 'gloves_chem',  label: 'Chemical-resistant gloves', emoji: '🧤', iso: 'M009' },
  { slug: 'boots',        label: 'Safety boots',              emoji: '🥾', iso: 'M008' },
  { slug: 'fall_arrest',  label: 'Fall-arrest harness',       emoji: '🪢', iso: 'M018' },
  { slug: 'fr_clothing',  label: 'FR clothing',               emoji: '🧥', iso: 'M010' },
];

const PPE_SLUGS = new Set(PPE_CATALOGUE.map((p) => p.slug));
const PPE_ORDER = new Map(PPE_CATALOGUE.map((p, i) => [p.slug, i]));

export function parsePpeList(text) {
  if (!text) return [];
  try {
    const parsed = typeof text === 'string' ? JSON.parse(text) : text;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s) => typeof s === 'string' && PPE_SLUGS.has(s));
  } catch {
    return [];
  }
}

export function serializePpeList(list) {
  if (!Array.isArray(list)) return '[]';
  const dedup = [...new Set(list.filter((s) => PPE_SLUGS.has(s)))];
  dedup.sort((a, b) => (PPE_ORDER.get(a) ?? 99) - (PPE_ORDER.get(b) ?? 99));
  return JSON.stringify(dedup);
}

export function ppeMeta(slug) {
  return PPE_CATALOGUE.find((p) => p.slug === slug) || null;
}

// ─── Applicable regulations catalogue (Chunk D) ────────────────────
// UK statutes / regs that map to the seed hazard library. Methodology-
// level standards (EN ISO 12100, ISO/TR 14121-2, HSWA, MHSWR) are
// always-on on the cover page so they're not in this catalogue.
export const REGS_CATALOGUE = [
  { slug: 'hswa',       short: 'HSWA 1974',            long: 'Health and Safety at Work Act 1974' },
  { slug: 'mhswr',      short: 'MHSWR 1999',           long: 'Management of Health and Safety at Work Regs 1999' },
  { slug: 'puwer',      short: 'PUWER 1998',           long: 'Provision and Use of Work Equipment Regs 1998' },
  { slug: 'loler',      short: 'LOLER 1998',           long: 'Lifting Operations and Lifting Equipment Regs 1998' },
  { slug: 'coshh',      short: 'COSHH 2002',           long: 'Control of Substances Hazardous to Health 2002' },
  { slug: 'wah',        short: 'WAH Regs 2005',        long: 'Work at Height Regs 2005' },
  { slug: 'dsear',      short: 'DSEAR 2002',           long: 'Dangerous Substances and Explosive Atmospheres Regs 2002' },
  { slug: 'eaw',        short: 'EAW 1989',             long: 'Electricity at Work Regs 1989' },
  { slug: 'noise',      short: 'Noise Regs 2005',      long: 'Control of Noise at Work Regs 2005' },
  { slug: 'hav',        short: 'HAV Regs 2005',        long: 'Control of Vibration at Work Regs 2005' },
  { slug: 'mho',        short: 'MHO Regs 1992',        long: 'Manual Handling Operations Regs 1992' },
  { slug: 'explosives', short: 'Explosives Regs 2014', long: 'Explosives Regs 2014' },
  { slug: 'firearms',   short: 'Firearms Act 1968',    long: 'Firearms Act 1968' },
  { slug: 'riddor',     short: 'RIDDOR 2013',          long: 'Reporting of Injuries, Diseases and Dangerous Occurrences Regs 2013' },
];

const REGS_SLUGS = new Set(REGS_CATALOGUE.map((r) => r.slug));
const REGS_ORDER = new Map(REGS_CATALOGUE.map((r, i) => [r.slug, i]));

export function parseRegsList(text) {
  if (!text) return [];
  try {
    const parsed = typeof text === 'string' ? JSON.parse(text) : text;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s) => typeof s === 'string' && REGS_SLUGS.has(s));
  } catch {
    return [];
  }
}

export function serializeRegsList(list) {
  if (!Array.isArray(list)) return '[]';
  const dedup = [...new Set(list.filter((s) => REGS_SLUGS.has(s)))];
  dedup.sort((a, b) => (REGS_ORDER.get(a) ?? 99) - (REGS_ORDER.get(b) ?? 99));
  return JSON.stringify(dedup);
}

export function regsMeta(slug) {
  return REGS_CATALOGUE.find((r) => r.slug === slug) || null;
}

// ─── Site notices catalogue (Chunk F reframed) ─────────────────────
// On-set callouts that describe the SHOOT (not the hazards). Selected
// notices render as a prominent amber banner on the PDF cover page.
export const SITE_NOTICES_CATALOGUE = [
  { slug: 'hot_set',      emoji: '🔥', label: 'Hot Set',                sub: 'No phones, radios, smoking, or hot works' },
  { slug: 'closed_set',   emoji: '🔒', label: 'Closed Set',             sub: 'Restricted access; no walk-throughs' },
  { slug: 'quiet',        emoji: '🤫', label: 'Quiet on Set',           sub: 'Filming in progress' },
  { slug: 'armed_action', emoji: '🔫', label: 'Live Firearms / Blanks', sub: 'Clear arc; PA "going hot"' },
  { slug: 'pyro_active',  emoji: '💥', label: 'Pyro Active',            sub: 'Clear exclusion zone; misfire wait' },
  { slug: 'wind_hold',    emoji: '💨', label: 'Wind Hold',              sub: 'Work paused above team threshold' },
  { slug: 'animals',      emoji: '🐎', label: 'Live Animals',           sub: 'Handler protocols in force' },
  { slug: 'minors',       emoji: '👶', label: 'Minor / Child Talent',   sub: 'Chaperone required' },
  { slug: 'night_shoot',  emoji: '🌙', label: 'Night Shoot',            sub: 'Lighting + transport considerations' },
  { slug: 'public',       emoji: '🏞️', label: 'Public Location',        sub: 'Members of public adjacent' },
];

const NOTICES_SLUGS = new Set(SITE_NOTICES_CATALOGUE.map((n) => n.slug));
const NOTICES_ORDER = new Map(SITE_NOTICES_CATALOGUE.map((n, i) => [n.slug, i]));

export function parseNoticesList(text) {
  if (!text) return [];
  try {
    const parsed = typeof text === 'string' ? JSON.parse(text) : text;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s) => typeof s === 'string' && NOTICES_SLUGS.has(s));
  } catch {
    return [];
  }
}

export function serializeNoticesList(list) {
  if (!Array.isArray(list)) return '[]';
  const dedup = [...new Set(list.filter((s) => NOTICES_SLUGS.has(s)))];
  dedup.sort((a, b) => (NOTICES_ORDER.get(a) ?? 99) - (NOTICES_ORDER.get(b) ?? 99));
  return JSON.stringify(dedup);
}

export function noticeMeta(slug) {
  return SITE_NOTICES_CATALOGUE.find((n) => n.slug === slug) || null;
}

// Canonical block categories. Matches the slugs used in the seed library
// (server/seed/riskAssessmentSeeds.js) so the tree-grouped hazard picker
// and Library view stay consistent. Bespoke blocks can opt-in to any of
// these, or stay uncategorised (null).
export const CATEGORY_OPTIONS = [
  { slug: 'site_movement',     label: 'Site Movement' },
  { slug: 'working_at_height', label: 'Working At Height' },
  { slug: 'mechanical',        label: 'Mechanical' },
  { slug: 'workshop',          label: 'Workshop' },
  { slug: 'pyro_fire_effects', label: 'Pyro / Fire Effects' },
  { slug: 'pressure_energy',   label: 'Pressure Energy' },
  { slug: 'electrical',        label: 'Electrical' },
  { slug: 'chemical',          label: 'Chemical' },
  { slug: 'environmental',     label: 'Environmental' },
  { slug: 'operations',        label: 'Operations' },
  { slug: 'rig_specific',      label: 'Rig Specific' },
];

// Override map for slugs whose titlecase doesn't read naturally (e.g. slashes).
// Most slugs render fine via the underscore-split rule below; add an entry
// here only when the default isn't right.
const CATEGORY_LABELS = {
  pyro_fire_effects: 'Pyro / Fire Effects',
};

// "wind" → "Wind", "working_at_height" → "Working At Height". Shared between
// RiskAssessmentLibrary (category section headers) and HazardAutocomplete
// (dropdown group labels).
export function titleCaseCategory(slug) {
  if (!slug) return 'Uncategorised';
  if (CATEGORY_LABELS[slug]) return CATEGORY_LABELS[slug];
  return String(slug)
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
